import { useEffect, useState } from 'react';
import { Cross, Doc, Download } from './AppIcons';
import { getDocumentContent } from '../../lib/entities/documents';
import { docxToPdf } from '../../lib/convert';
import '../../styles/app-documents.css';

/** The held file this picker exports. Both the detail page and the requirements list can supply it. */
export interface ExportTarget {
  documentId: string;
  fileName: string;
  contentType: string;
}

/** Trigger a browser download of `blob` under `fileName` (no library, no navigation). */
function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download picker. reeve keeps one canonical stored copy; the user takes it away as a Word document (the
 * stored bytes as-is) or as a PDF (converted client-side on demand). Either way the download is named
 * after the stored document with the chosen extension — so a file uploaded as "handbook.pdf" comes back
 * as "handbook.pdf" / "handbook.docx", never the internal name. Bytes are fetched same-origin.
 *
 * <p>Shared between the document detail page and the requirements list, so Download behaves identically
 * wherever it is offered — the list's quick action is the same flow, not a shortcut past it.</p>
 */
export default function ExportModal({
  instance,
  onClose,
}: {
  instance: ExportTarget;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<null | 'docx' | 'pdf'>(null);
  const [error, setError] = useState<string | null>(null);
  const base = instance.fileName.replace(/\.[^.]+$/, '') || 'document';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const run = async (kind: 'docx' | 'pdf') => {
    if (busy) return;
    setBusy(kind);
    setError(null);
    try {
      const buffer = await getDocumentContent(instance.documentId);
      if (kind === 'docx') {
        saveBlob(new Blob([buffer], { type: instance.contentType }), `${base}.docx`);
      } else {
        saveBlob(await docxToPdf(buffer, instance.fileName), `${base}.pdf`);
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'We couldn’t prepare that download. Please try again.',
      );
      setBusy(null);
    }
  };

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Download document"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Cross size={18} />
        </button>
        <h2 className="modal__title">Download</h2>
        <p className="docs-modal__sub">
          Choose a format. Your file downloads as “<strong>{base}</strong>”.
        </p>

        <div className="docs-export">
          <button
            type="button"
            className="docs-export__opt"
            onClick={() => void run('docx')}
            disabled={!!busy}
          >
            <span className="docs-export__ico">
              <Doc size={18} />
            </span>
            <span className="docs-export__txt">
              <span className="docs-export__name">Word document</span>
              <span className="docs-export__sub">{base}.docx</span>
            </span>
            {busy === 'docx' && <span className="docs-export__spin">…</span>}
          </button>

          <button
            type="button"
            className="docs-export__opt"
            onClick={() => void run('pdf')}
            disabled={!!busy}
          >
            <span className="docs-export__ico">
              <Download size={18} />
            </span>
            <span className="docs-export__txt">
              <span className="docs-export__name">PDF</span>
              <span className="docs-export__sub">{base}.pdf</span>
            </span>
            {busy === 'pdf' && <span className="docs-export__spin">…</span>}
          </button>
        </div>

        {error && (
          <p className="docs-export__err" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
